import { useEffect, useRef, useState } from "react";
import { api, fileSrc } from "../lib/apiClient";
import {
  X, Play, RotateCcw, Sparkles, ListChecks, CalendarClock,
  Search, Send, Check, Plus, Bell, User, Video, Square,
} from "lucide-react";
import { useReelRecorder } from "./useReelRecorder";

const IMG = "https://static.prod-images.emergentagent.com/jobs/5d950d92-6b67-4808-826a-961251c2afb8/images/";
const PHOTOS = {
  home: IMG + "d0bf7caf38b06ca6b93a237cc8e366b2e3e5c8fc7834a2ac20fdd66cc9da5477.png",
  newEngland: IMG + "bbe5226141376f60f9159d28030d125945975ced6b352ac70b25a175085f3762.png",
  bedroom: IMG + "49282ca7f782ced793b1b128d18d13f160c4abeac26820d53849a0127cfe244c.png",
  sconce: IMG + "18cc864e25b03ca813eb79565b6dd328f7884e9d07c5b5af7b5dc5c2ebf092d2.png",
  living2: IMG + "317fb7225f92575b95c7689bd98ad66085d74561aec1cbf7f4bb361f57980f4c.png",
  dining: IMG + "d3d07cede007f737b124e0c341652723bed3613141ebb4ff80db4f2e910f36bc.png",
};

const REVEAL_MS = 1500;
const FALLBACK_MS = 7000;

const SCENES = [
  { type: "intro", label: "", narration: "This is WorkRoom, the all in one platform where interior designers run every project." },
  { type: "dashboard", label: "Your studio dashboard", narration: "Your entire studio on one dashboard. Track status, budgets, and progress at a glance." },
  { type: "overview", label: "New Canaan · Coastal Remodel", narration: "Open a project, like the New Canaan coastal remodel, and see every detail from concept to install." },
  { type: "mood", label: "Mood Boards", narration: "Design mood boards in minutes, importing inspiration straight from Pinterest." },
  { type: "palette", label: "Paint Palettes", narration: "Generate a professional paint palette from your imagery in one click." },
  { type: "ffe", label: "FF&E · Smart Import", narration: "Add furnishings with smart import. Paste a link, and the price, vendor, and details fill in automatically." },
  { type: "budget", label: "Budget", narration: "Manage budgets and allocations with total clarity." },
  { type: "calendar", label: "Calendar & Tasks", narration: "Assign tasks, set due dates, and keep every milestone on schedule." },
  { type: "notes", label: "Team Messaging", narration: "Message your whole team and your clients in one place." },
  { type: "ai", label: "AI Design Assistant", narration: "And an A I design assistant helps you source, plan, and stay ahead." },
  { type: "cta", label: "", narration: "WorkRoom. Run your design business beautifully. Start your free trial at workroom admin dot com." },
];

/* ---------- vertical app frame ---------- */
const VFrame = ({ children, tab }) => (
  <div className="absolute inset-0 flex flex-col bg-[#F9F8F6]">
    <div className="h-12 shrink-0 bg-white border-b border-[#EBE5E0] flex items-center justify-between px-4">
      <span className="font-serif text-xl text-[#2B2622]">WorkRoom</span>
      <div className="flex items-center gap-2">
        <span className="text-[9px] uppercase tracking-wider text-[#A68A6B]">Elise Sinclair</span>
        <div className="w-7 h-7 bg-[#A68A6B] text-white flex items-center justify-center font-serif text-sm">E</div>
      </div>
    </div>
    {tab && (
      <div className="shrink-0 bg-white border-b border-[#EBE5E0] px-4 flex gap-3 overflow-hidden">
        {["Overview", "Budget", "FF&E", "Mood Boards", "Calendar", "Notes", "AI"].map((t) => (
          <span key={t} className={`py-2.5 text-[9px] uppercase tracking-wider whitespace-nowrap border-b-2 ${t === tab ? "border-[#A68A6B] text-[#2B2622]" : "border-transparent text-[#9b938b]"}`}>{t}</span>
        ))}
      </div>
    )}
    <div className="flex-1 overflow-hidden p-4">{children}</div>
  </div>
);

const PhaseBar = ({ active }) => {
  const phases = ["Discovery", "Concept", "Procurement", "Install", "Done"];
  return (
    <div className="flex items-center gap-1.5 mt-3">
      {phases.map((p, i) => (
        <div key={p} className="flex-1">
          <div className={`h-1.5 rounded-full ${i <= active ? "bg-[#A68A6B]" : "bg-[#EBE5E0]"}`} />
          <p className={`mt-1 text-[7px] uppercase tracking-wide ${i === active ? "text-[#A68A6B] font-semibold" : "text-[#9b938b]"}`}>{p}</p>
        </div>
      ))}
    </div>
  );
};

/* ---------- scenes ---------- */
function Dashboard({ r }) {
  const cards = [
    { name: "New Canaan", sub: "Coastal Remodel", phase: "Procurement", pct: 62, chk: "7 / 11", photo: PHOTOS.home, hot: true },
    { name: "Beacon Hill Townhouse", sub: "Full Renovation", phase: "Design Concept", pct: 34, chk: "3 / 9", photo: PHOTOS.newEngland },
    { name: "Harborview Penthouse", sub: "Furnishing", phase: "Installation", pct: 88, chk: "12 / 14", photo: PHOTOS.bedroom },
  ];
  return (
    <VFrame>
      <p className="text-[10px] uppercase tracking-[0.25em] text-[#A68A6B]">Your workspace</p>
      <h1 className="font-serif text-3xl text-[#2B2622] mt-0.5 mb-4">Projects</h1>
      <div className="space-y-3">
        {cards.map((c) => (
          <div key={c.name} className={`bg-white border overflow-hidden transition-all duration-500 ${c.hot && r ? "border-[#A68A6B] shadow-lg" : "border-[#EBE5E0]"}`}>
            <div className="flex">
              <div className="w-24 bg-cover bg-center shrink-0" style={{ backgroundImage: `url(${c.photo})` }} />
              <div className="p-3 flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1 text-[8px] uppercase tracking-wider text-[#A68A6B]"><span className="w-1 h-1 rounded-full bg-[#A68A6B]" />{c.phase}</span>
                  {c.hot && <span className="inline-flex items-center gap-1 bg-[#A68A6B] text-white text-[7px] uppercase tracking-wider px-1 py-0.5"><Bell size={7} />2 new</span>}
                </div>
                <h3 className="font-serif text-lg text-[#2B2622] mt-1 leading-tight truncate">{c.name}</h3>
                <p className="text-[10px] text-[#9b938b]">{c.sub}</p>
                <div className="mt-2">
                  <div className="flex justify-between text-[8px] text-[#5C5651] mb-0.5"><span className="uppercase tracking-wider text-[#A68A6B]">Checklists</span><span>{c.chk}</span></div>
                  <div className="h-1 bg-[#F2EFEB]"><div className="h-full bg-[#A68A6B]" style={{ width: `${c.pct}%` }} /></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </VFrame>
  );
}

function Overview() {
  return (
    <VFrame tab="Overview">
      <p className="text-[10px] uppercase tracking-[0.25em] text-[#A68A6B]">Discovery → Install</p>
      <h1 className="font-serif text-3xl text-[#2B2622] mt-0.5">New Canaan</h1>
      <p className="text-xs text-[#5C5651]">Coastal Remodel · The Whitmore Family</p>
      <div className="h-28 bg-cover bg-center border border-[#EBE5E0] mt-3" style={{ backgroundImage: `url(${PHOTOS.home})` }} />
      <PhaseBar active={2} />
      <div className="mt-3 bg-white border border-[#EBE5E0] p-3">
        <div className="flex items-center gap-1.5 text-[#A68A6B] mb-2"><ListChecks size={13} /><span className="text-[9px] uppercase tracking-wider">Checklists / Assignments</span></div>
        {[["Confirm coastal palette", true], ["Order brass sconces (x4)", true], ["Finalize FF&E budget", false]].map(([t, d], i) => (
          <div key={i} className="flex items-center gap-2 py-1">
            <span className={`w-3.5 h-3.5 border flex items-center justify-center ${d ? "bg-[#A68A6B] border-[#A68A6B]" : "border-[#C9BBAE]"}`}>{d && <Check size={9} className="text-white" />}</span>
            <span className={`text-xs ${d ? "line-through text-[#9b938b]" : "text-[#2B2622]"}`}>{t}</span>
          </div>
        ))}
      </div>
    </VFrame>
  );
}

function Mood({ r }) {
  return (
    <VFrame tab="Mood Boards">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif text-lg text-[#2B2622]">Coastal Living</h2>
        <span className="inline-flex items-center gap-1 bg-[#A68A6B] text-white text-[8px] uppercase tracking-wider px-2 py-1.5"><Plus size={10} /> Pinterest</span>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="h-32 bg-cover bg-center" style={{ backgroundImage: `url(${PHOTOS.living2})` }} />
        <div className="h-32 bg-cover bg-center" style={{ backgroundImage: `url(${PHOTOS.bedroom})` }} />
        <div className="h-32 bg-cover bg-center" style={{ backgroundImage: `url(${PHOTOS.dining})` }} />
        <div className={`h-32 bg-cover bg-center transition-all duration-700 ${r ? "opacity-100 scale-100 ring-2 ring-[#A68A6B]" : "opacity-0 scale-90"}`} style={{ backgroundImage: `url(${PHOTOS.newEngland})` }} />
      </div>
      <p className="text-[10px] text-[#9b938b] mt-2">Imported from Pinterest · New England coastal</p>
    </VFrame>
  );
}

function Palette({ r }) {
  const sw = [["Chantilly Lace", "OC-65", "#F4F2EC"], ["White Sand", "OC-10", "#E8DECB"], ["Salted Lake", "1601", "#B7C4C6"], ["Newburg Green", "HC-158", "#3E5A5B"], ["Hale Navy", "HC-154", "#3B4A57"], ["Weathered Oak", "—", "#B99C74"]];
  return (
    <VFrame tab="Mood Boards">
      <div className="h-24 bg-cover bg-center border border-[#EBE5E0] mb-3" style={{ backgroundImage: `url(${PHOTOS.newEngland})` }} />
      <div className="flex items-center gap-1.5 text-[#A68A6B] mb-2"><Sparkles size={13} /><span className="text-[9px] uppercase tracking-wider">Generated paint palette</span></div>
      <div className="grid grid-cols-2 gap-2">
        {sw.map((s, i) => (
          <div key={i} className={`border border-[#EBE5E0] bg-white flex items-center gap-2 transition-all duration-500 ${r ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`} style={{ transitionDelay: `${i * 80}ms` }}>
            <div className="w-10 h-10 shrink-0" style={{ background: s[2] }} />
            <div className="min-w-0"><p className="text-[10px] text-[#2B2622] leading-tight truncate">{s[0]}</p><p className="text-[8px] text-[#9b938b]">BM {s[1]}</p></div>
          </div>
        ))}
      </div>
    </VFrame>
  );
}

function FFE({ r }) {
  return (
    <VFrame tab="FF&E">
      <div className="flex gap-3">
        <div className="w-28 shrink-0">
          <div className="bg-white border border-[#EBE5E0] p-1.5"><div className="h-28 bg-cover bg-center" style={{ backgroundImage: `url(${PHOTOS.sconce})` }} /></div>
        </div>
        <div className="flex-1 flex items-center gap-1.5 bg-white border border-[#EBE5E0] px-2 py-2 self-start">
          <Search size={12} className="text-[#A68A6B] shrink-0" />
          <span className="text-[9px] text-[#5C5651] truncate">visualcomfort.com/…</span>
          <span className="ml-auto bg-[#A68A6B] text-white text-[8px] uppercase px-1.5 py-1">Fetch</span>
        </div>
      </div>
      <div className="mt-3 bg-white border border-[#EBE5E0] p-3">
        <div className="flex items-center gap-1.5 text-[#A68A6B] mb-2"><Sparkles size={12} /><span className="text-[9px] uppercase tracking-wider">Smart import</span></div>
        {[["Item", "Brass Sconce, Cream Shade"], ["Vendor", "Visual Comfort"], ["Quantity", "4"], ["Unit price", "$429.00"], ["Total", "$1,716.00"]].map(([k, v], i) => (
          <div key={i} className={`flex justify-between py-1.5 border-b border-[#F2EFEB] transition-all duration-500 ${r ? "opacity-100" : "opacity-0"}`} style={{ transitionDelay: `${i * 70}ms` }}>
            <span className="text-[9px] uppercase tracking-wider text-[#9b938b]">{k}</span>
            <span className={`text-xs ${k === "Total" ? "font-serif text-base text-[#2B2622]" : "text-[#2B2622]"}`}>{v}</span>
          </div>
        ))}
      </div>
    </VFrame>
  );
}

function Budget() {
  const rows = [["Furnishings", 42000, 62], ["Lighting", 12000, 48], ["Textiles", 9500, 30], ["Millwork", 18000, 74], ["Contingency", 6000, 12]];
  return (
    <VFrame tab="Budget">
      <div className="flex items-end justify-between mb-3">
        <div><p className="text-[9px] uppercase tracking-wider text-[#A68A6B]">Allocated</p><h2 className="font-serif text-2xl text-[#2B2622]">$87,500</h2></div>
        <div className="text-right"><p className="text-[9px] uppercase tracking-wider text-[#A68A6B]">Spent</p><p className="font-serif text-xl text-[#8C7154]">$41,320</p></div>
      </div>
      <div className="bg-white border border-[#EBE5E0] divide-y divide-[#F2EFEB]">
        {rows.map(([cat, amt, pct], i) => (
          <div key={i} className="px-3 py-2.5">
            <div className="flex justify-between text-xs mb-1"><span className="text-[#2B2622]">{cat}</span><span className="text-[#5C5651]">${amt.toLocaleString()}</span></div>
            <div className="h-1.5 bg-[#F2EFEB]"><div className="h-full bg-[#A68A6B]" style={{ width: `${pct}%` }} /></div>
          </div>
        ))}
      </div>
    </VFrame>
  );
}

function Calendar({ r }) {
  return (
    <VFrame tab="Calendar">
      <div className="flex items-center gap-1.5 text-[#A68A6B] mb-3"><CalendarClock size={14} /><span className="text-[9px] uppercase tracking-wider">Upcoming tasks</span></div>
      {[["Site walkthrough — procurement", "Jun 12", true], ["Sconce install approval", "Jun 18", false], ["Client budget review", "Jun 24", false]].map(([t, d, isNew], i) => (
        <div key={i} className={`flex items-center gap-3 bg-white border border-[#EBE5E0] px-3 py-3 mb-2 transition-all duration-500 ${(isNew && !r) ? "opacity-0 -translate-x-3" : "opacity-100"}`}>
          <div className="text-center w-10 shrink-0"><p className="font-serif text-lg text-[#A68A6B] leading-none">{String(d).split(" ")[1]}</p><p className="text-[7px] uppercase text-[#9b938b]">{String(d).split(" ")[0]}</p></div>
          <p className="text-xs text-[#2B2622]">{t}{isNew && <span className="ml-2 text-[7px] uppercase tracking-wider bg-[#F5EAD9] text-[#8C7154] px-1 py-0.5">new</span>}</p>
        </div>
      ))}
    </VFrame>
  );
}

function Notes({ r }) {
  return (
    <VFrame tab="Notes">
      <div className="bg-white border border-[#EBE5E0] p-3">
        <p className="text-xs text-[#2B2622] leading-relaxed">Team &amp; Clara — let's hold a budget review Thursday at 2pm. The coastal palette is approved and procurement is underway. We're on track for installation.</p>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F2EFEB]">
          <span className="inline-flex items-center gap-1 text-[8px] uppercase tracking-wider text-[#A68A6B]"><span className="w-1.5 h-1.5 bg-[#506B52]" /> To: Everyone</span>
          <span className={`inline-flex items-center gap-1 text-white text-[8px] uppercase tracking-wider px-3 py-1.5 transition-colors ${r ? "bg-[#506B52]" : "bg-[#A68A6B]"}`}>{r ? <><Check size={10} /> Sent</> : <><Send size={10} /> Send</>}</span>
        </div>
      </div>
      <p className="text-[9px] text-[#9b938b] mt-2">Category: Budget · notifies team + client by email</p>
    </VFrame>
  );
}

function AI({ r }) {
  return (
    <VFrame tab="AI">
      <div className="flex justify-end mb-2"><div className="bg-[#A68A6B] text-white text-[11px] px-3 py-2 max-w-[85%]">Where can I source an antique dining set near New Canaan?</div></div>
      <div className={`transition-all duration-500 ${r ? "opacity-100" : "opacity-0"}`}>
        <div className="bg-[#F2EFEB] text-[#2B2622] text-[11px] px-3 py-2 leading-relaxed max-w-[92%]">
          Try <b>Elephant's Trunk Flea Market</b> (Sundays), the <b>Stamford Estate Sale</b>, and <b>Woodbury Antiques Weekend</b> — great for a solid oak trestle table.
        </div>
        {r && <p className="text-[9px] text-[#506B52] mt-1.5">✓ Saved to Notes</p>}
      </div>
    </VFrame>
  );
}

function BrandCard({ cta }) {
  return (
    <div className="absolute inset-0 bg-[#2B2622] flex flex-col items-center justify-center text-center px-8">
      <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: `url(${PHOTOS.home})` }} />
      <div className="relative">
        <p className="text-[11px] uppercase tracking-[0.4em] text-[#C9A87C] mb-4">{cta ? "Run your studio beautifully" : "For interior designers"}</p>
        <h1 className="font-serif text-white tracking-wide" style={{ fontSize: cta ? "3.2rem" : "3.8rem", lineHeight: 1.05 }}>WorkRoom</h1>
        {cta ? (
          <>
            <p className="text-[#E7D8C5] mt-6 text-lg font-serif">Start your free trial</p>
            <div className="mt-5 inline-block border border-[#C9A87C] text-[#E7D8C5] uppercase tracking-widest text-sm px-6 py-3">workroomadmin.com</div>
          </>
        ) : (
          <p className="text-[#D9CBBF] mt-5 text-lg font-serif italic">The all-in-one platform for interior designers.</p>
        )}
      </div>
    </div>
  );
}

function Scene({ s, r }) {
  switch (s.type) {
    case "intro": return <BrandCard cta={false} />;
    case "cta": return <BrandCard cta />;
    case "dashboard": return <Dashboard r={r} />;
    case "overview": return <Overview />;
    case "mood": return <Mood r={r} />;
    case "palette": return <Palette r={r} />;
    case "ffe": return <FFE r={r} />;
    case "budget": return <Budget />;
    case "calendar": return <Calendar r={r} />;
    case "notes": return <Notes r={r} />;
    case "ai": return <AI r={r} />;
    default: return null;
  }
}

export default function IGReel({ onClose }) {
  const [clips, setClips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [idx, setIdx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const audioRef = useRef(null);
  const timers = useRef([]);
  const { recording, start: recStart, stop: recStop } = useReelRecorder("workroom-instagram-reel");
  const recMode = useRef(false);

  useEffect(() => {
    api.post("/admin/promo-voiceover", { scenes: SCENES.map((s) => s.narration), voice: "nova" })
      .then((r) => setClips(r.data.clips || []))
      .catch(() => setClips([]))
      .finally(() => setLoading(false));
  }, []);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  useEffect(() => {
    if (!started) return;
    if (idx >= SCENES.length) {
      if (recMode.current) { recMode.current = false; recStop(); setStarted(false); setIdx(0); }
      else onClose();
      return;
    }
    clearTimers();
    setRevealed(false);
    timers.current.push(setTimeout(() => setRevealed(true), REVEAL_MS));
    const url = clips[idx];
    const advance = () => setIdx((p) => (p === idx ? idx + 1 : p));
    if (url && audioRef.current) {
      audioRef.current.src = fileSrc(url);
      audioRef.current.onended = advance;
      audioRef.current.play().catch(() => timers.current.push(setTimeout(advance, FALLBACK_MS)));
    } else {
      timers.current.push(setTimeout(advance, FALLBACK_MS));
    }
    return clearTimers;
    // eslint-disable-next-line
  }, [idx, started]);

  const start = () => {
    setStarted(true);
    setIdx(0);
  };
  const recordAndPlay = async () => {
    const ok = await recStart();
    if (!ok) return;
    recMode.current = true;
    setStarted(true);
    setIdx(0);
  };
  const stopRecording = () => { recMode.current = false; recStop(); clearTimers(); if (audioRef.current) audioRef.current.pause(); setStarted(false); setIdx(0); };
  const restart = () => { clearTimers(); if (audioRef.current) audioRef.current.pause(); setIdx(0); setStarted(true); };

  const s = SCENES[Math.min(idx, SCENES.length - 1)];

  return (
    <div className="fixed inset-0 z-[100] bg-black/92 flex items-center justify-center p-4" data-testid="ig-reel">
      <audio ref={audioRef} className="hidden" />
      <button data-testid="ig-close" onClick={onClose} className="absolute top-5 right-6 text-white/70 hover:text-white z-20"><X size={26} /></button>

      <div className="flex flex-col items-center">
        <div className="relative bg-[#1F1B17] overflow-hidden shadow-2xl" style={{ aspectRatio: "9 / 16", height: "min(88vh, 780px)" }} data-testid="ig-frame">
          {!started ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center bg-[#2B2622] px-8">
              <div className="absolute inset-0 opacity-25 bg-cover bg-center" style={{ backgroundImage: `url(${PHOTOS.home})` }} />
              <div className="relative">
                <p className="text-[11px] uppercase tracking-[0.35em] text-[#C9A87C] mb-3">Instagram Reel · 9:16</p>
                <h2 className="font-serif text-4xl text-white leading-tight">The app,<br />in action</h2>
                <p className="text-[#D9CBBF] mt-3 text-sm">~75 seconds · voiceover</p>
                <button data-testid="ig-play" onClick={start} disabled={loading}
                  className="mt-6 inline-flex items-center gap-2 bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-sm px-8 py-4 transition-colors disabled:opacity-60">
                  <Play size={18} /> {loading ? "Preparing…" : "Play reel"}
                </button>
                <button data-testid="ig-record" onClick={recordAndPlay} disabled={loading}
                  className="mt-3 inline-flex items-center gap-2 border border-[#A68A6B] text-white/90 hover:bg-[#A68A6B] uppercase tracking-wider text-sm px-6 py-3 transition-colors disabled:opacity-60">
                  <Video size={17} /> Record & download
                </button>
                <p className="text-[11px] text-[#9b938b] mt-4 px-6">Screen-record this frame, or "Record & download" to save a video with voiceover (pick this tab & tick "share tab audio").</p>
              </div>
            </div>
          ) : (
            <>
              <div key={idx} className="absolute inset-0 animate-[fadeIn_0.5s_ease]"><Scene s={s} r={revealed} /></div>
              <div className="absolute top-0 left-0 right-0 flex gap-1 p-2 z-10">
                {SCENES.map((_, i) => (
                  <div key={i} className="h-1 flex-1 bg-black/20 overflow-hidden rounded-full">
                    <div className="h-full bg-[#A68A6B]" style={{ width: i <= idx ? "100%" : "0%" }} />
                  </div>
                ))}
              </div>
              {s.label && (
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-5 py-4">
                  <p className="text-white font-serif text-lg text-center">{s.label}</p>
                </div>
              )}
            </>
          )}
        </div>

        {started && (
          <div className="flex items-center justify-center gap-4 mt-4">
            <button data-testid="ig-restart" onClick={restart} className="inline-flex items-center gap-2 text-white/70 hover:text-white text-xs uppercase tracking-wider"><RotateCcw size={15} /> Restart</button>
            {recording && (
              <button data-testid="ig-stop-record" onClick={stopRecording} className="inline-flex items-center gap-2 text-[#E7A0A0] hover:text-white text-xs uppercase tracking-wider">
                <Square size={13} className="fill-current" /> Stop & save
              </button>
            )}
            <span className="text-white/40 text-xs">{Math.min(idx + 1, SCENES.length)} / {SCENES.length}</span>
          </div>
        )}
      </div>
    </div>
  );
}
